import { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Drawer,
  Empty,
  Form,
  Input,
  List,
  Space as AntSpace,
  Spin,
  Tag,
  Typography,
} from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { getApiErrorMessage } from '@api/server';
import MarkdownView from '../markdown/markdown-view';
import { searchSpaceChunks } from './spaces';

const { TextArea } = Input;
const { Text, Paragraph } = Typography;

const SPACE_SEARCH_DRAWER_WIDTH_KEY = 'botva.spaces.searchDrawerWidth';
const SPACE_CHUNK_DRAWER_WIDTH_KEY = 'botva.spaces.chunkDrawerWidth';
const MIN_DRAWER_WIDTH = 480;
const DEFAULT_SEARCH_DRAWER_WIDTH = 640;
const DEFAULT_CHUNK_DRAWER_WIDTH = 720;
const DRAWER_VIEWPORT_GAP = 48;

const getInitialDrawerWidth = (storageKey, defaultWidth) => {
  const savedWidth = Number(localStorage.getItem(storageKey));
  if (Number.isFinite(savedWidth) && savedWidth >= MIN_DRAWER_WIDTH) {
    return savedWidth;
  }

  return defaultWidth;
};

const clampDrawerWidth = (width) => {
  const maxWidth = Math.max(
    MIN_DRAWER_WIDTH,
    window.innerWidth - DRAWER_VIEWPORT_GAP,
  );

  return Math.min(Math.max(width, MIN_DRAWER_WIDTH), maxWidth);
};

const formatChunkMetadata = (metadata) => {
  if (metadata == null || metadata === '') {
    return '';
  }

  if (typeof metadata === 'object') {
    return JSON.stringify(metadata, null, 2);
  }

  const raw = String(metadata);
  try {
    return JSON.stringify(JSON.parse(raw), null, 2);
  } catch {
    return raw;
  }
};

const useDrawerResize = (storageKey, defaultWidth) => {
  const [drawerWidth, setDrawerWidth] = useState(() => (
    getInitialDrawerWidth(storageKey, defaultWidth)
  ));

  const handleResizeStart = useCallback((event) => {
    event.preventDefault();

    const handleMouseMove = (moveEvent) => {
      const nextWidth = clampDrawerWidth(window.innerWidth - moveEvent.clientX);
      setDrawerWidth(nextWidth);
      localStorage.setItem(storageKey, String(nextWidth));
    };

    const handleMouseUp = () => {
      document.body.classList.remove('drawer-resizing');
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };

    document.body.classList.add('drawer-resizing');
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
  }, [storageKey]);

  return { drawerWidth, handleResizeStart };
};

const SpaceSearch = ({ space, open, onClose }) => {
  const [form] = Form.useForm();
  const [error, setError] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [results, setResults] = useState([]);
  const [hasSearched, setHasSearched] = useState(false);
  const [selectedChunk, setSelectedChunk] = useState(null);
  const {
    drawerWidth: searchDrawerWidth,
    handleResizeStart: handleSearchResizeStart,
  } = useDrawerResize(SPACE_SEARCH_DRAWER_WIDTH_KEY, DEFAULT_SEARCH_DRAWER_WIDTH);
  const {
    drawerWidth: chunkDrawerWidth,
    handleResizeStart: handleChunkResizeStart,
  } = useDrawerResize(SPACE_CHUNK_DRAWER_WIDTH_KEY, DEFAULT_CHUNK_DRAWER_WIDTH);

  useEffect(() => {
    if (!open) {
      setSelectedChunk(null);
      return;
    }

    form.setFieldsValue({ text: '' });
    setError('');
    setResults([]);
    setHasSearched(false);
    setSelectedChunk(null);
  }, [form, open, space?.id]);

  const handleSearch = useCallback(async (values) => {
    if (!space?.id) {
      return;
    }

    const text = values.text?.trim();
    if (!text) {
      setError('Введите текст запроса.');
      return;
    }

    setError('');
    setIsSearching(true);
    setHasSearched(true);
    try {
      const chunks = await searchSpaceChunks(space.id, text);
      setResults(Array.isArray(chunks) ? chunks : []);
    } catch (requestError) {
      setResults([]);
      setError(getApiErrorMessage(requestError));
    } finally {
      setIsSearching(false);
    }
  }, [space?.id]);

  const title = space
    ? `Поиск · ${space.name}`
    : 'Поиск';

  const chunkTitle = selectedChunk
    ? [
      'Чанк',
      selectedChunk.articleid != null ? `статья #${selectedChunk.articleid}` : null,
      selectedChunk.idx != null ? `#${selectedChunk.idx}` : null,
      selectedChunk.score != null ? String(selectedChunk.score) : null,
    ].filter(Boolean).join(' · ')
    : 'Чанк';
  const chunkMetadata = formatChunkMetadata(selectedChunk?.metadata);

  return (
    <>
      <Drawer
        className="user-drawer user-form-drawer agent-resizable-drawer"
        title={title}
        placement="right"
        width={searchDrawerWidth}
        open={open}
        onClose={onClose}
        destroyOnHidden
        footer={(
          <div className="user-drawer-footer">
            <Button onClick={onClose}>Закрыть</Button>
          </div>
        )}
      >
        <div
          className="agent-drawer-resize-handle"
          role="separator"
          aria-orientation="vertical"
          aria-label="Изменить ширину окна поиска"
          onMouseDown={handleSearchResizeStart}
        />

        {error && (
          <Alert className="user-alert" type="error" message={error} showIcon />
        )}

        <Form
          form={form}
          layout="vertical"
          onFinish={handleSearch}
          requiredMark={false}
          initialValues={{ text: '' }}
        >
          <Form.Item
            label="Запрос"
            name="text"
            rules={[{ required: true, message: 'Введите текст запроса.' }]}
          >
            <TextArea
              placeholder="Текст запроса пользователя"
              autoSize={{ minRows: 3, maxRows: 8 }}
              autoFocus
            />
          </Form.Item>

          <Form.Item>
            <AntSpace>
              <Button
                type="primary"
                icon={<SearchOutlined />}
                loading={isSearching}
                onClick={() => form.submit()}
              >
                Искать
              </Button>
            </AntSpace>
          </Form.Item>
        </Form>

        <Spin spinning={isSearching}>
          {!hasSearched ? (
            <Empty description="Введите запрос и нажмите «Искать»" />
          ) : results.length === 0 ? (
            <Empty description="Чанки не найдены" />
          ) : (
            <List
              className="space-search-results"
              header={<Text type="secondary">Найдено: {results.length}</Text>}
              dataSource={results}
              renderItem={(chunk) => (
                <List.Item
                  className="space-search-result-item"
                  onClick={() => setSelectedChunk(chunk)}
                >
                  <div className="space-search-result">
                    <div className="space-search-result-meta">
                      <Tag color="blue">
                        {chunk.score == null ? '—' : chunk.score}
                      </Tag>
                      <Text type="secondary">
                        статья #{chunk.articleid}
                        {chunk.idx == null ? '' : ` · chunk ${chunk.idx}`}
                      </Text>
                    </div>
                    <Paragraph className="space-search-result-text" ellipsis={{ rows: 4 }}>
                      {chunk.text || '—'}
                    </Paragraph>
                  </div>
                </List.Item>
              )}
            />
          )}
        </Spin>
      </Drawer>

      <Drawer
        className="user-drawer user-form-drawer agent-resizable-drawer"
        title={chunkTitle}
        placement="right"
        width={chunkDrawerWidth}
        open={Boolean(selectedChunk)}
        onClose={() => setSelectedChunk(null)}
        destroyOnHidden
        footer={(
          <div className="user-drawer-footer">
            <Button onClick={() => setSelectedChunk(null)}>Закрыть</Button>
          </div>
        )}
      >
        <div
          className="agent-drawer-resize-handle"
          role="separator"
          aria-orientation="vertical"
          aria-label="Изменить ширину окна чанка"
          onMouseDown={handleChunkResizeStart}
        />

        <div className="space-chunk-section">
          <Text strong>Metadata</Text>
          {chunkMetadata ? (
            <pre className="space-chunk-metadata">{chunkMetadata}</pre>
          ) : (
            <Text type="secondary">Metadata пусто</Text>
          )}
        </div>

        <div className="space-chunk-section">
          <Text strong>Текст</Text>
          {selectedChunk?.text ? (
            <MarkdownView className="markdown-preview space-chunk-markdown">
              {selectedChunk.text}
            </MarkdownView>
          ) : (
            <Text type="secondary">Текст чанка пуст</Text>
          )}
        </div>
      </Drawer>
    </>
  );
};

export default SpaceSearch;
